// Self-hosted Capgo OTA orchestration — native-only, additive, web-safe.
//
// capacitor.config.ts runs the updater in 'onlyDownload' mode: Capgo auto-checks
// and downloads bundles and emits events, but NEVER applies on its own. This
// module decides per-update how to apply:
//   - seamless update -> CapacitorUpdater.next({ id })  (applies on next launch)
//   - forced update   -> CapacitorUpdater.set({ id })   (applies now; set() self-reloads)
//
// A deploy marks a bundle "forced" by suffixing its version with FORCE_SUFFIX
// (see deploy_vhd_app.sh --force). We read that off the incoming bundle.version.
//
// UI: there is NO launch splash. The app opens straight to its content. A loader
// overlay (OtaLoader) is shown ONLY while a FORCED update is downloading/applying:
//   - forced detected -> emit 'legallumo:ota-forcing'  (loader appears + spinner)
//   - download progress -> emit 'legallumo:ota-progress'
//   - forced failed/timed out -> emit 'legallumo:ota-forcing-cancelled' (loader hides)
// Seamless updates and "no update" launches show no overlay at all.

import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

const isNative = () => Capacitor.isNativePlatform();

// A forced bundle's version ends with this marker (added by the deploy script).
export const FORCE_SUFFIX = '-force';

// If a forced download never completes, stop blocking and fall back to the current
// bundle instead of trapping the user on the loader.
const FORCING_TIMEOUT_MS = 30000;

let _initialized = false;

// A forced update is in progress: the loader is up and we'll reload on completion.
let forcing = false;
let forcingDispatched = false; // 'legallumo:ota-forcing' already emitted (fire once)

// Show the forced-update loader. Idempotent — the 'download' event fires many
// times, so the overlay event is emitted only once.
function showLoader() {
  forcing = true;
  if (!forcingDispatched) {
    forcingDispatched = true;
    window.dispatchEvent(new Event('legallumo:ota-forcing'));
  }
}

// Hide the loader and let the app continue on the current bundle (forced update
// failed or timed out). No-op if the loader was never shown.
function hideLoader() {
  if (!forcing) return;
  forcing = false;
  window.dispatchEvent(new Event('legallumo:ota-forcing-cancelled'));
}

function isForcedVersion(version) {
  return typeof version === 'string' && version.endsWith(FORCE_SUFFIX);
}

export function initOtaUpdater() {
  if (!isNative() || _initialized) return;
  _initialized = true;

  // Download progress. This is also the earliest place we know the incoming
  // bundle's version, so a forced download raises the loader here (before
  // downloadComplete) and starts feeding it progress.
  CapacitorUpdater.addListener('download', (e) => {
    if (isForcedVersion(e?.bundle?.version)) showLoader();
    if (forcing) {
      window.dispatchEvent(
        new CustomEvent('legallumo:ota-progress', { detail: { percent: e?.percent ?? 0 } })
      );
    }
  }).catch(() => {});

  // An update is available (UX-only hint). Forced -> ensure the loader is up.
  // Seamless -> do nothing (it stages silently in the background).
  CapacitorUpdater.addListener('updateAvailable', (e) => {
    if (isForcedVersion(e?.bundle?.version)) showLoader();
  }).catch(() => {});

  // Download finished. Authoritative apply point: derive force vs seamless from the
  // bundle's own version, never from a flag another event may or may not have set.
  CapacitorUpdater.addListener('downloadComplete', async (e) => {
    const bundle = e?.bundle;
    const id = bundle?.id;
    if (!id) return;
    const forced = isForcedVersion(bundle?.version);
    try {
      if (forced) {
        showLoader(); // keep the loader up through the reload
        // set() sets the current bundle AND immediately reloads the app (no reload() needed).
        await CapacitorUpdater.set({ id });
      } else {
        // Seamless: stage for next launch. A late downloadComplete must NOT
        // surprise-reload mid-session, and shows no UI.
        await CapacitorUpdater.next({ id });
      }
    } catch (err) {
      // Apply failed -> don't trap the user; drop the loader and stay on current.
      hideLoader();
    }
  }).catch(() => {});

  // Failures: never leave the user stuck on the loader.
  CapacitorUpdater.addListener('downloadFailed', () => hideLoader()).catch(() => {});
  CapacitorUpdater.addListener('updateFailed', () => hideLoader()).catch(() => {});

  // Safety: give up on a stuck forced download and fall back to the current bundle.
  setTimeout(() => {
    if (forcing) hideLoader();
  }, FORCING_TIMEOUT_MS);
}
